import React, { useCallback } from 'react';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Types
import { Attachment, AttachmentFilesTypes } from 'Shared/types/Attachment.type';

// Utils
import * as AttachmentUtils from 'Shared/utils/Attachment.utils';

// Components
import { WpDropdown, WpDropdownToggle, WpDropdownMenu, WpDropdownMenuItem } from 'Wpds/components/dropdown';
import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';
import VideoPlayer from './AttachmentTypes/VideoPlayer';
import AttachmentTypeLogo from './AttachmentTypeLogo';
import AttachmentTypeIcon from './AttachmentTypeIcon';
import AttachmentViewerModal from './AttachmentModal/AttachmentViewerModal';

import './AttachmentItem.scss';

type Props = {
  attachment: Attachment;
  previewOpen: boolean;
  handleDeleteAttachment?: Function;
  handleUpdateShowPreview?: Function;
  handleDownload?: Function;
  setActiveAttachment?: Function;
};

const AttachmentItem = ({
  attachment,
  previewOpen,
  handleDeleteAttachment,
  handleUpdateShowPreview,
  handleDownload,
}: Props) => {
  const { user: currentUser } = useCurrentUser();

  const [isAttachmentModalOpen, toggleIsAttachmentModalOpen] = useToggle(false);
  const url = attachment.url;
  const fileName = attachment.fileName;
  const fileType = AttachmentUtils.getFileType({ attachment });
  const attachmentUploader = attachment.user;

  const attachmentUploaderId = attachmentUploader.id;
  const canDelete = handleDeleteAttachment && attachmentUploaderId === currentUser.id;

  const showPreview = previewOpen;
  const classNamePrefix = 'AttachmentItem';

  const handleDeleteClick = useCallback(() => {
    const id = attachment.id;
    handleDeleteAttachment(id);
  }, [handleDeleteAttachment, attachment]);

  const handleDownloadClick = useCallback(() => {
    handleDownload(attachment);
  }, [handleDownload, attachment]);

  const handlePreviewClick = () => {
    if (
      fileType === AttachmentFilesTypes.IMAGE ||
      fileType === AttachmentFilesTypes.VIDEO ||
      fileType === AttachmentFilesTypes.AUDIO
    ) {
      toggleIsAttachmentModalOpen();
    } else {
      handleDownloadClick();
    }
  };

  const PreviewTemplate = ({ icon }: { icon: string }) => {
    return (
      <>
        <div className={`${classNamePrefix}__preview__hover-effect`}>
          <WpIcon name={icon} size="lg" />
        </div>
        <div className={`${classNamePrefix}__preview__file`}>
          <AttachmentTypeLogo attachment={attachment} className={`${classNamePrefix}__preview__file__logo`} />
        </div>
      </>
    );
  };

  const getPreview = () => {
    switch (fileType) {
      case AttachmentFilesTypes.AUDIO:
        return <PreviewTemplate icon="volume-up" />;
      case AttachmentFilesTypes.IMAGE:
        return (
          <>
            <div className={`${classNamePrefix}__preview__hover-effect`}>
              <WpIcon name="eye" size="lg" />
            </div>
            <img className={`${classNamePrefix}__preview__img`} src={url} alt={fileName} />
          </>
        );
      case AttachmentFilesTypes.VIDEO:
        return (
          <>
            <div className={`${classNamePrefix}__preview__hover-effect`}>
              <WpIcon name="play" prefix="fas" size="lg" />
            </div>
            <div className={`${classNamePrefix}__preview__file`}>
              <VideoPlayer
                className={`${classNamePrefix}__preview__video`}
                attachment={attachment}
                options={{ controls: false }}
              />
              <AttachmentTypeLogo attachment={attachment} className={`${classNamePrefix}__preview__file__logo`} />
            </div>
          </>
        );
      default:
        return <PreviewTemplate icon="download" />;
    }
  };

  return (
    <>
      <div className={classNamePrefix}>
        {showPreview && (
          <div className={`${classNamePrefix}__preview`} onClick={handlePreviewClick}>
            {getPreview()}
          </div>
        )}
        <div className={`${classNamePrefix}__footer`}>
          <div className={`${classNamePrefix}__footer__info`} onClick={handlePreviewClick}>
            <AttachmentTypeIcon attachment={attachment} />
            <div className={`${classNamePrefix}__footer__info__filename`}>{fileName}</div>
          </div>
          <div className={`${classNamePrefix}__footer__actions`}>
            <WpDropdown placement="bottom-end">
              <WpDropdownToggle>
                <WpButton size="sm" variant="tertiary">
                  <WpIcon name="ellipsis-h-alt" />
                </WpButton>
              </WpDropdownToggle>
              <WpDropdownMenu>
                {handleUpdateShowPreview && (
                  <WpDropdownMenuItem onClick={handleUpdateShowPreview}>
                    <WpIcon name="eye" />
                    {previewOpen ? 'Hide Preview' : 'Show Preview'}
                  </WpDropdownMenuItem>
                )}
                <WpDropdownMenuItem onClick={handleDownloadClick}>
                  <WpIcon name="download" />
                  Download
                </WpDropdownMenuItem>
                {canDelete && (
                  <WpDropdownMenuItem onClick={handleDeleteClick} color="danger">
                    <WpIcon name="trash" />
                    Delete
                  </WpDropdownMenuItem>
                )}
              </WpDropdownMenu>
            </WpDropdown>
          </div>
        </div>
      </div>
      <AttachmentViewerModal
        isOpen={isAttachmentModalOpen}
        attachment={attachment}
        toggleModal={toggleIsAttachmentModalOpen}
        handleDownload={handleDownloadClick}
      />
    </>
  );
};

export default AttachmentItem;
